/** "Logged as" timestamp formatting, shared by the case-action modals. `timeZone` is optional so stories
 *  and tests can pin a zone instead of recomputing the expectation from the call under test. */

/** Hold's compact one-line form: `Jul 13, 12:49 PM`. */
export const formatLoggedAtShort = (at: Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  }).format(at);

/**
 * Exclude's form, returned as SEPARATE parts: `{ date: '07/13/2026', time: '10:42 AM' }`.
 *
 * Two reasons this returns parts rather than one joined string. (1) The ticket's "Logged As" section
 * enumerates three distinct data points (user, date, time) and its own example prints them separately; the
 * modal renders each in its own node so a test can assert them individually rather than substring-matching
 * one opaque blob. (2) A single combined `Intl.DateTimeFormat` cannot produce the required separator
 * anyway — it emits `07/13/2026, 10:42 AM` with a comma — so composing from two formatters is required
 * regardless, and returning the pieces is strictly more useful than joining them here.
 */
export const formatLoggedAtParts = (
  at: Date,
  timeZone?: string
): { readonly date: string; readonly time: string } => ({
  date: new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone
  }).format(at),
  time: new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  }).format(at)
});
